/**
 * accountApi — the fetches behind "Save your progress" (docs/accounts/PLAN.md, web only).
 *
 *  - `fetchAuthConfig`: which ways in the server can offer right now. Cached for the page's life
 *    once it answers, because every prompt asks and the answer only changes with a deploy. A failure
 *    is not cached, and reads as "nothing on offer", so a dead server hides the prompts rather than
 *    showing buttons that cannot work.
 *  - `startEmailLogin`: asks for a magic link. The address goes to the server and nowhere else —
 *    never into analytics, never into the store.
 *  - `signOut`: clears the cookie, drops the stored token and reloads into a fresh guest.
 *  - `trackAccountEvent`: the two client events the server whitelists for this flow.
 *  - `submitDisplayName`: the one-time name pick after a first email sign-in.
 *
 * Same auth rule as `profileApi`: the session JWT as a Bearer, from the store first.
 */
import { isGuestName } from '@spades/shared';
import { apiPath } from '../bridge/embedded';
import { readStoredToken, writeStoredToken } from '../bridge/identity';
import type { Player } from './types-local';
import { store } from '../store/store';

export interface AuthConfig {
  discordWeb: boolean;
  email: boolean;
}

export const NO_AUTH: AuthConfig = { discordWeb: false, email: false };

/** Where a prompt was shown or a sign-in started. Mirrors the server's `where` values. */
export type ClaimWhere = 'match_end' | 'profile' | 'settings';

/**
 * Why a link was not sent. One reason per status the route documents, each with its own copy:
 *  - `invalid`     → 400. Not an address.
 *  - `rate`        → 429. Too many tries from this browser or for this address.
 *  - `unavailable` → 503. Email sign-in is off on this server.
 *  - `send`        → 502. The mail provider refused it.
 *  - `offline`     → anything else, including a dead socket.
 */
export type EmailStartFailure = 'invalid' | 'rate' | 'unavailable' | 'send' | 'offline';

function bearer(): string | null {
  try {
    const fromStore = store.getState().session.token;
    if (fromStore) return fromStore;
  } catch {
    /* store not built yet */
  }
  return readStoredToken();
}

function authHeaders(): Record<string, string> {
  const token = bearer();
  return token ? { authorization: `Bearer ${token}` } : {};
}

let configCache: Promise<AuthConfig> | null = null;

export function fetchAuthConfig(): Promise<AuthConfig> {
  configCache ??= fetch(apiPath('/api/auth/config'), { cache: 'no-store' })
    .then((r) => (r.ok ? (r.json() as Promise<Partial<AuthConfig>>) : Promise.reject(new Error(String(r.status)))))
    .then((b) => ({ discordWeb: b.discordWeb === true, email: b.email === true }))
    .catch(() => {
      configCache = null;
      return NO_AUTH;
    });
  return configCache;
}

/** Tests only: forget the cached config. */
export function resetAuthConfigCache(): void {
  configCache = null;
}

/** Full-page navigation target for "Continue with Discord". Not a fetch: it is an OAuth redirect. */
export function discordWebUrl(): string {
  return apiPath('/api/auth/discord/web');
}

export async function startEmailLogin(email: string): Promise<{ ok: true } | { ok: false; reason: EmailStartFailure }> {
  let res: Response;
  try {
    res = await fetch(apiPath('/api/auth/email/start'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ email }),
    });
  } catch {
    return { ok: false, reason: 'offline' };
  }
  if (res.ok) return { ok: true };
  if (res.status === 400) return { ok: false, reason: 'invalid' };
  if (res.status === 429) return { ok: false, reason: 'rate' };
  if (res.status === 503) return { ok: false, reason: 'unavailable' };
  if (res.status === 502) return { ok: false, reason: 'send' };
  return { ok: false, reason: 'offline' };
}

export function emailStartFailureText(reason: EmailStartFailure): string {
  switch (reason) {
    case 'invalid':
      return "That doesn't look like an email address.";
    case 'rate':
      return 'Too many links asked for. Give it a few minutes.';
    case 'unavailable':
      return 'Email sign-in is off right now. Try Discord instead.';
    case 'send':
      return "We couldn't send that email. Try again in a moment.";
    default:
      return 'Could not reach the server.';
  }
}

/**
 * Sign out on this browser. The reload is the point: the next boot finds no cookie and no stored
 * token, and the server hands out a fresh guest. The token is dropped even if the call fails —
 * a sign-out that leaves the player signed in is worse than one that leaves a stale cookie.
 */
export async function signOut(reload: () => void = () => location.reload()): Promise<void> {
  try {
    await fetch(apiPath('/api/auth/logout'), { method: 'POST', credentials: 'include', headers: authHeaders() });
  } catch {
    /* offline: the local token still goes */
  }
  writeStoredToken(null);
  reload();
}

type AccountEvent =
  | { ev: 'claim_prompt_shown'; props: { where: ClaimWhere } }
  | { ev: 'claim_started'; props: { method: 'discord' | 'email'; where: ClaimWhere } };

/**
 * Fire-and-forget, `keepalive` so `claim_started` survives the Discord redirect that follows it.
 * The route is bearer-only; a guest always has one on the web.
 */
export function trackAccountEvent(event: AccountEvent): void {
  try {
    void fetch(apiPath('/api/track'), {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      credentials: 'include',
      body: JSON.stringify(event),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* analytics never breaks a flow */
  }
}

/**
 * May this player pick a name? Email accounts still wearing their guest's `Guest-XXXX` only — so a
 * pick, which breaks the pattern, is the end of it. Same rule as the server's 409.
 */
export function canPickName(me: Pick<Player, 'kind' | 'name'> | null | undefined): boolean {
  return me?.kind === 'email' && isGuestName(me.name);
}

/**
 * Why a name was refused. The first four are the shared validator's reasons, echoed by the server's
 * 400; `taken` is the 409 (a name was already picked, usually in another tab), which the sheet
 * answers by closing quietly rather than with copy.
 */
export type NameFailure = 'length' | 'chars' | 'reserved' | 'blocked' | 'taken' | 'unauth' | 'rate' | 'offline';

const NAME_REASONS: ReadonlySet<string> = new Set(['length', 'chars', 'reserved', 'blocked']);

export async function submitDisplayName(
  name: string,
): Promise<{ ok: true; token: string; name: string } | { ok: false; reason: NameFailure }> {
  let res: Response;
  try {
    res = await fetch(apiPath('/api/account/name'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ name }),
    });
  } catch {
    return { ok: false, reason: 'offline' };
  }
  let body: { token?: unknown; user?: { name?: unknown; displayName?: unknown }; reason?: unknown } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* an empty or HTML body: the status still says enough */
  }
  if (res.ok && typeof body.token === 'string' && body.token) {
    const picked = body.user?.displayName ?? body.user?.name;
    return { ok: true, token: body.token, name: typeof picked === 'string' && picked ? picked : name };
  }
  if (res.status === 400) {
    const reason = typeof body.reason === 'string' && NAME_REASONS.has(body.reason) ? (body.reason as NameFailure) : 'chars';
    return { ok: false, reason };
  }
  if (res.status === 409) return { ok: false, reason: 'taken' };
  if (res.status === 401) return { ok: false, reason: 'unauth' };
  if (res.status === 429) return { ok: false, reason: 'rate' };
  return { ok: false, reason: 'offline' };
}

export function nameFailureText(reason: NameFailure | string): string {
  switch (reason) {
    case 'length':
      return '2 to 20 characters.';
    case 'chars':
      return "Letters, numbers, spaces and . _ - ' only.";
    case 'reserved':
      return 'That name is taken by the house. Try another.';
    case 'blocked':
      return 'Pick a different name.';
    case 'unauth':
      return 'Your sign-in ran out. Reload and try again.';
    case 'rate':
      return 'Too many tries. Give it a minute.';
    default:
      return 'Could not reach the server.';
  }
}
